import { useCallback, useRef } from "react";
import type { FieldErrors, FieldPath, UseFormSetValue, UseFormTrigger } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { focusInput } from "../../helpers";
import { AppStorage } from "../../services/AppStorage";
import type { InvoiceFormValues, ReservationFormParams } from "../../store/draftReservation/types";

import { Block, Input, MaskType } from "@stepapp/ui";

type Props = {
  setValue?: UseFormSetValue<ReservationFormParams>;
  errors?: FieldErrors<ReservationFormParams>;
  trigger?: UseFormTrigger<ReservationFormParams>;
  name: string;
  taxIdentification: string;
  line1: string;
  line2: string;
  postal_code: string;
  city: string;
  email: string;
};

const VATInvoiceForm = ({
  setValue,
  errors,
  trigger,
  name,
  taxIdentification,
  line1,
  line2,
  postal_code,
  city,
  email,
}: Props) => {
  const countryCode = AppStorage().get("countryCode");
  const { t } = useTranslation();
  const nipNumberRef = useRef(null);
  const addressFirstLineRef = useRef(null);
  const addressSecondLineRef = useRef(null);
  const postalCodeRef = useRef(null);
  const cityRef = useRef(null);
  const emailRef = useRef(null);
  const nipNumber = taxIdentification.slice(2);
  const country = countryCode === "NO" ? "NO" : "PL";

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const onChange = useCallback((key: FieldPath<InvoiceFormValues>, value: string) => {
    setValue?.(key, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const handleNipChange = useCallback(
    (value: string) => {
      const onlyNumbers = value.replace(/[^0-9]/g, "");

      setValue?.("billingAddress.taxIdentification", `${country}${onlyNumbers}`, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [country],
  );

  return (
    <Block>
      <Input
        editable={!!setValue}
        error={errors?.billingAddress?.name?.message}
        onBlur={() => trigger?.("billingAddress.name")}
        onChangeText={(e) => onChange("billingAddress.name", e)}
        onSubmitEditing={focusInput(nipNumberRef)}
        placeholder={t("components.input.placeholder.companyName")}
        value={name}
      />
      <Input
        editable={!!setValue}
        error={errors?.billingAddress?.taxIdentification?.message}
        keyboardType="number-pad"
        onBlur={() => trigger?.("billingAddress.taxIdentification")}
        onChangeText={handleNipChange}
        onSubmitEditing={focusInput(addressFirstLineRef)}
        placeholder={t("components.input.placeholder.NIPNumber")}
        ref={nipNumberRef}
        value={nipNumber}
      />

      <Input
        editable={!!setValue}
        error={errors?.billingAddress?.line1?.message}
        onBlur={() => trigger?.("billingAddress.line1")}
        onChangeText={(e) => onChange("billingAddress.line1", e)}
        onSubmitEditing={focusInput(addressSecondLineRef)}
        placeholder={t("components.input.placeholder.addressFirstLine")}
        ref={addressFirstLineRef}
        value={line1}
      />
      <Input
        editable={!!setValue}
        onBlur={() => trigger?.("billingAddress.line2")}
        onChangeText={(e) => onChange("billingAddress.line2", e)}
        onSubmitEditing={focusInput(postalCodeRef)}
        placeholder={t("components.input.placeholder.addressSecondLine")}
        ref={addressSecondLineRef}
        value={line2}
      />

      <Block row flex={1}>
        <Block flex={1} marginRight={2}>
          <Input
            editable={!!setValue}
            error={errors?.billingAddress?.postal_code?.message}
            mask={MaskType.PostalCode}
            countryCode={AppStorage().get("countryCode")}
            onBlur={() => trigger?.("billingAddress.postal_code")}
            onChangeText={(e) => onChange("billingAddress.postal_code", e)}
            onSubmitEditing={focusInput(cityRef)}
            placeholder={t("components.input.placeholder.postalCode")}
            ref={postalCodeRef}
            translateLabelX={-5}
            value={postal_code}
          />
        </Block>
        <Block flex={1}>
          <Input
            editable={!!setValue}
            error={errors?.billingAddress?.city?.message}
            onBlur={() => trigger?.("billingAddress.city")}
            onChangeText={(e) => onChange("billingAddress.city", e)}
            onSubmitEditing={focusInput(emailRef)}
            placeholder={t("components.input.placeholder.city")}
            ref={cityRef}
            style={styles.inputInRow}
            translateLabelX={-5}
            value={city}
          />
        </Block>
      </Block>
      <Input
        editable={!!setValue}
        error={errors?.billingAddress?.email?.message}
        onBlur={() => trigger?.("billingAddress.email")}
        onChangeText={(e) => onChange("billingAddress.email", e)}
        placeholder={t("components.input.placeholder.email")}
        ref={emailRef}
        style={styles.inputInRow}
        value={email}
      />
    </Block>
  );
};

export default VATInvoiceForm;

const styles = StyleSheet.create({
  inputInRow: {
    flex: 1,
  },
});
